// src/pages/Lawyer/LawyerDeletedPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Card, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import PaginatedTable from "../Common/PaginatedTable";
import EntityDetailsSidebar from "../../components/Common/EntityDetailsSidebar";
import Unauthorized from "../Common/Unauthorized";
import usePermission from "../../hooks/usePermission";
import { formatDateTime } from "../../utils/formatters";
import {
  getDeletedLawyersPaged,
  restoreLawyer,
} from "../../services/lawyerService";
import {
  UserX,
  Eye,
  IdCard,
  Mail,
  Briefcase,
  Building2,
  CalendarClock,
  RotateCcw,
} from "lucide-react";

/**
 * Página: Lista paginada de abogados eliminados (borrado lógico)
 * - Búsqueda por nombre, email, documento o tarjeta profesional
 * - Sidebar con detalles
 * - Botón "Restaurar" visible solo si el usuario tiene permiso
 * - Refresca automáticamente la grilla tras restaurar
 */
const LawyerDeletedPage = () => {
  const hasPermission = usePermission();

  // Estado de tabla y búsqueda
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // Sidebar de detalles
  const [selectedItem, setSelectedItem] = useState(null);

  // Esquema para el panel lateral de detalles
  const schema = [
    { label: "Nombre completo", key: "fullName" },
    { label: "Email", key: "email" },
    { label: "Tipo documento", key: "documentTypeCode" },
    { label: "Número documento", key: "documentNumber" },
    { label: "Tarjeta profesional", key: "professionalLicenseNumber" },
    { label: "Empresa (tenant)", key: "companyDisplayName" },
    { label: "Eliminado por", key: "deletedBy" },
    { label: "Eliminado el", key: "deletedOn", render: formatDateTime },
  ];

  /** Carga de datos (paginada + búsqueda) */
  const fetchDeleted = useCallback(async (page, size, search = "") => {
    try {
      const resp = await getDeletedLawyersPaged(page, size, search);
      if (resp?.success) {
        setRows(resp.data.items || []);
        setTotalRows(resp.data.totalCount || 0);
      } else {
        throw new Error(resp?.message || "Respuesta no exitosa");
      }
    } catch {
      Swal.fire(
        "Error",
        "No se pudieron cargar los abogados eliminados.",
        "error"
      );
    }
  }, []);

  useEffect(() => {
    fetchDeleted(currentPage, perPage, searchTerm);
  }, [fetchDeleted, currentPage, perPage, searchTerm]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  /** Acción: restaurar un abogado */
  const handleRestore = async (row) => {
    const result = await Swal.fire({
      title: "¿Restaurar abogado?",
      text: `Se habilitará nuevamente el acceso de ${
        row.fullName ?? "este abogado"
      }.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, restaurar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const resp = await restoreLawyer(row.id);

      if (resp?.success) {
        Swal.fire(
          "Restaurado",
          resp.message || "El abogado fue restaurado correctamente.",
          "success"
        );

        // Refrescar la tabla y cerrar detalles
        fetchDeleted(currentPage, perPage, searchTerm);
        if (selectedItem?.id === row.id) setSelectedItem(null);
      } else {
        // Mostrar el mensaje del backend aunque success sea false
        Swal.fire(
          "Atención",
          resp?.message || "No se pudo restaurar el abogado.",
          "warning"
        );
      }
    } catch (err) {
      // Capturar error con su mensaje
      const backendMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Ocurrió un error inesperado.";

      Swal.fire("Error", backendMsg, "error");
    }
  };

  // Permiso de vista
  if (!hasPermission("abogados.view_deleted")) return <Unauthorized />;

  /** Definición de columnas del datagrid */
  const columns = [
    {
      name: "Nombre",
      selector: (row) => row.fullName || "—",
      sortable: true,
      wrap: true,
      left: true,
    },
    {
      name: "Documento",
      selector: (row) => row.documentNumber,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <IdCard size={16} className="text-muted" />
          <span>
            {row.documentTypeCode || "—"} {row.documentNumber || ""}
          </span>
        </div>
      ),
    },
    {
      name: "Tarjeta Profesional",
      selector: (row) => row.professionalLicenseNumber || "—",
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <Briefcase size={16} className="text-muted" />
          <span>{row.professionalLicenseNumber || "—"}</span>
        </div>
      ),
    },
    {
      name: "Eliminado el",
      selector: (row) => row.deletedOn,
      sortable: true,
      wrap: true,
      width: "260px",
      center: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <CalendarClock size={16} className="text-muted" />
          <span>{formatDateTime(row.deletedOn)}</span>
        </div>
      ),
    },
    {
      name: "Acciones",
      width: hasPermission("abogados.restore") ? "190px" : "120px",
      center: true,
      button: true,
      ignoreRowClick: true,
      allowOverflow: true,
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-info"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItem(row);
            }}
            title="Ver detalles"
          >
            <Eye size={16} />
          </Button>

          {hasPermission("abogados.restore") && (
            <Button
              variant="outline-success"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleRestore(row);
              }}
              title="Restaurar"
            >
              <RotateCcw size={16} />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid mt-4 px-4 w-100">
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-dark d-flex align-items-center gap-2">
            <UserX size={22} /> Abogados eliminados
          </h4>
        </div>

        <PaginatedTable
          keyField="id"
          columns={columns}
          data={rows}
          totalRows={totalRows}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePerRowsChange}
          onSearch={handleSearch}
        />

        <EntityDetailsSidebar
          isVisible={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          title="Detalles del abogado eliminado"
          icon={<UserX size={20} />}
          data={selectedItem}
          schema={schema}
        />
      </Card>
    </div>
  );
};

export default LawyerDeletedPage;
